import { ref } from 'vue'
import { NotifyError } from 'js/vue/boot/Notify'

// Slide 16:9 do PowerPoint, com o mesmo fundo do LayoutBase
const SLIDE = {
  width: 1920,
  height: 1080,
  padding: 48,
  backgroundColor: '#fafbfb',
}

/**
 * @description Troca cada <use> do sprite pelo conteúdo do <symbol> correspondente.
 * O html-to-image clona cada <svg> inteiro sem aplicar estilos nos filhos, então o
 * <use> precisa virar paths de verdade para o currentColor herdar a cor do ícone.
 * @param {HTMLElement} node - Nó que será capturado
 * @returns {Function} - Função que restaura os <use> originais
 */
function inlineSpriteUses(node) {
  const svgNS = 'http://www.w3.org/2000/svg'
  const replaced = []

  node.querySelectorAll('use').forEach((use) => {
    const href = use.getAttribute('xlink:href') || use.getAttribute('href')
    const symbol = href?.startsWith('#') && document.querySelector(href)
    if (!symbol) return

    // Leva os atributos do <symbol> (viewBox, fill...) para o <svg> aninhado
    const inline = document.createElementNS(svgNS, 'svg')
    ;[...symbol.attributes]
      .filter(({ name }) => name !== 'id')
      .forEach(({ name, value }) => inline.setAttribute(name, value))
    inline.setAttribute('width', '100%')
    inline.setAttribute('height', '100%')
    symbol.childNodes.forEach((child) =>
      inline.appendChild(child.cloneNode(true))
    )

    use.replaceWith(inline)
    replaced.push([inline, use])
  })

  return () => replaced.forEach(([inline, use]) => inline.replaceWith(use))
}

/**
 * @description Desenha um canvas centralizado dentro de um quadro de tamanho fixo, mantendo a proporção
 * @param {HTMLCanvasElement} source - Canvas com a captura
 * @param {Object} frame - Quadro de destino
 * @param {number} frame.width - Largura em px
 * @param {number} frame.height - Altura em px
 * @param {number} [frame.padding=0] - Margem interna em px
 * @param {string} [frame.backgroundColor='#fff'] - Cor de fundo do quadro
 * @param {number} pixelRatio - Densidade usada na captura
 * @returns {HTMLCanvasElement} - Canvas no tamanho do quadro
 */
function fitCanvasToFrame(source, frame, pixelRatio) {
  const { width, height, padding = 0, backgroundColor = '#fff' } = frame

  const canvas = document.createElement('canvas')
  canvas.width = width * pixelRatio
  canvas.height = height * pixelRatio

  const context = canvas.getContext('2d')
  context.fillStyle = backgroundColor
  context.fillRect(0, 0, canvas.width, canvas.height)

  const inner = padding * pixelRatio
  const scale = Math.min(
    (canvas.width - inner * 2) / source.width,
    (canvas.height - inner * 2) / source.height
  )
  const drawWidth = source.width * scale
  const drawHeight = source.height * scale

  context.imageSmoothingQuality = 'high'
  context.drawImage(
    source,
    (canvas.width - drawWidth) / 2,
    (canvas.height - drawHeight) / 2,
    drawWidth,
    drawHeight
  )

  return canvas
}

/**
 * @description Composable para capturar um nó do DOM como imagem e fazer o download
 * @returns {Object} - Objeto com o estado exporting e a função downloadNodeAsPng
 */
export default function useImageExport() {
  const exporting = ref(false)

  /**
   * @description Captura um nó do DOM como PNG e faz o download.
   * Elementos com o atributo data-export-ignore ficam de fora da imagem.
   * @param {HTMLElement} node - Nó a ser capturado
   * @param {string} fileName - Nome do arquivo, ex.: 'slide.png'
   * @param {Object} [options] - Opções do html-to-image (pixelRatio, backgroundColor...)
   * @param {Object} [options.fitTo] - Quadro fixo onde a captura é encaixada, ex.: { width: 1920, height: 1080 }
   * @returns {Promise<void>}
   */
  async function downloadNodeAsPng(node, fileName, options = {}) {
    const { fitTo, filter, ...captureOptions } = options
    const pixelRatio =
      captureOptions.pixelRatio || window.devicePixelRatio || 1
    let restoreUses

    exporting.value = true

    try {
      const { toCanvas } = await import('html-to-image')
      restoreUses = inlineSpriteUses(node)

      await document.fonts.ready
      const capture = await toCanvas(node, {
        cacheBust: true,
        ...captureOptions,
        pixelRatio,
        filter: (el) =>
          !el.hasAttribute?.('data-export-ignore') && (!filter || filter(el)),
      })

      const canvas = fitTo
        ? fitCanvasToFrame(capture, fitTo, pixelRatio)
        : capture

      const link = document.createElement('a')
      link.download = fileName
      link.href = canvas.toDataURL('image/png')
      link.click()
    } catch (error) {
      console.error('Error exporting image:', error)
      NotifyError('Não foi possível gerar a imagem.')
    } finally {
      restoreUses?.()
      exporting.value = false
    }
  }

  /**
   * @description Captura um nó do DOM e baixa a imagem encaixada num slide 16:9 (1920x1080)
   * @param {HTMLElement} node - Nó a ser capturado
   * @param {string} fileName - Nome do arquivo, ex.: 'slide.png'
   * @returns {Promise<void>}
   */
  function downloadNodeAsSlide(node, fileName) {
    return downloadNodeAsPng(node, fileName, {
      pixelRatio: 2,
      backgroundColor: SLIDE.backgroundColor,
      fitTo: SLIDE,
    })
  }

  return {
    exporting,
    downloadNodeAsPng,
    downloadNodeAsSlide,
  }
}
